<template>
  <div class="pregame">
    <div class="row justify-content-center">
      <div class="col-lg-8 col-md-10 col-12">

        <!-- ======== Group section ======== -->
        <div class="pregame-section gt-section">
          <div class="pregame-section__header">
            <div class="pregame-section__label">
              <i class="tim-icons icon-single-02 text-white"></i>
              Group
            </div>
          </div>

          <!-- Empty state -->
          <div v-if="model.users.length <= 0" class="pregame-empty">
            <span class="pregame-empty__icon">
              <i class="tim-icons icon-single-02"></i>
            </span>
            <p class="pregame-empty__text">
              No players available. Make sure everyone in the group has checked in.
            </p>
          </div>

          <!-- Player list -->
          <div v-if="model.users.length > 0" :key="k" class="player-list">
            <div
              v-for="(user, index) in model.users"
              :key="'player-' + index"
              class="player-item gt-list-item"
              :class="{ 'player-item--confirmed gt-list-item--selected': selectedUser[index] }"
            >
              <div class="player-item__avatar">
                {{ getInitials(user.first_name, user.last_name) }}
              </div>
              <div class="player-item__content">
                <span class="player-item__name">
                  {{ user.first_name }} {{ user.last_name }}
                </span>
                <span v-if="user.is_minor == 1" class="player-item__badge">minor</span>
              </div>
              <button
                class="player-item__toggle gt-pill-btn"
                :class="{ 'player-item__toggle--active gt-pill-btn--active': selectedUser[index] }"
                @click="togglePlayer(index)"
              >
                <i class="tim-icons" :class="selectedUser[index] ? 'icon-check-2' : 'icon-simple-add'"></i>
                {{ selectedUser[index] ? 'Confirmed' : 'Confirm' }}
              </button>
            </div>
          </div>

          <!-- Team name (inline within group section) -->
          <div v-if="model.users.length > 0 && hasFieldEnabled(8)" class="pregame-team-field">
            <label class="pregame-team-field__label">Team name</label>
            <base-input v-model="model.team" placeholder="Team name"></base-input>
          </div>

        </div>

        <!-- ======== Start time ======== -->
        <div class="pregame-section gt-section">
          <div class="pregame-section__header">
            <div class="pregame-section__label">
              <i class="tim-icons icon-watch-time text-white"></i>
              Start time
            </div>
          </div>
          <div class="pregame-time-wrapper">
            <div class="pregame-time-group">
              <base-input class="mb-0 pregame-time-picker">
                <el-time-picker v-on:input="validateTime()"
                  v-model="model.start_time"
                  :format="picker_time_format" placeholder="HH:MM"
                ></el-time-picker>
              </base-input>
              <div class="pregame-now-append gt-now-append" @click="setNow()">
                Now
              </div>
            </div>
            <div v-if="modelValidations.start_time" class="pregame-time-error">
              {{ modelValidations.start_time }}
            </div>
          </div>
        </div>

        <!-- ======== Pre-event notes ======== -->
        <div class="pregame-section gt-section" v-if="hasFieldEnabled(9) || bookingSlotIdForTags">
          <div class="pregame-section__header">
            <div class="pregame-section__label">
              <i class="tim-icons icon-paper text-white"></i>
              Pre-event notes
            </div>
          </div>
          <base-input v-if="hasFieldEnabled(9)" :error="getError('notes')">
            <textarea
              class="form-control pregame-textarea gt-textarea"
              placeholder="Start typing..."
              name="notes"
              v-model="model.pre_notes"
              v-validate="modelValidations.pre_notes"
              rows="3"></textarea>
          </base-input>
          <!-- Tags on the group's booking (same pattern as the booking drawer) -->
          <div v-if="bookingSlotIdForTags" class="pregame-team-field">
            <label class="pregame-team-field__label">Tags</label>
            <div class="tags-row">
              <template v-if="tagsLoading">
                <span class="skel skel--chip"></span>
                <span class="skel skel--chip"></span>
                <span class="skel skel--chip"></span>
              </template>
              <template v-else>
              <span
                v-for="tag in bookingTags"
                :key="'TG' + tag.id"
                class="tag-chip text-white"
                :class="{ 'tag-chip--archived': tag.is_archived }"
                :style="tagChipStyle(tag.color)"
              >
                {{ tag.name }}
                <button
                  class="tag-chip__remove"
                  :disabled="savingTags"
                  :aria-label="'Remove tag ' + tag.name"
                  @click="removeTag(tag)"
                >
                  <i class="tim-icons icon-simple-remove"></i>
                </button>
              </span>
              <el-dropdown trigger="click" @command="addTag">
                <span class="tag-chip tag-chip--add">
                  <i v-if="savingTags" class="el-icon-loading mr-1" style="color: #e14eca;"></i>
                  <i v-else class="tim-icons icon-simple-add mr-1"></i>
                  Add tag
                </span>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item v-for="tag in availableTags" :key="'AT' + tag.id" :command="tag.id">
                    <span class="tag-chip__dot mr-1" :style="{ backgroundColor: tag.color }"></span>
                    {{ tag.name }}
                  </el-dropdown-item>
                  <el-dropdown-item v-if="availableTags.length === 0" disabled>No more tags</el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
              </template>
            </div>
          </div>
        </div>

        <!-- ======== Corporate booking ======== -->
        <div class="pregame-section gt-section" :key="j + 'ICB'" v-if="hasFieldEnabled(7)">
          <div class="pregame-switch-row">
            <div class="pregame-section__label">
              <i class="tim-icons icon-badge text-white"></i>
              Corporate booking
            </div>
            <el-switch active-color="#e14eca" v-on:input="j++"
                       v-model="model.is_corporate_booking" />
          </div>
        </div>

        <!-- ======== Team experience ======== -->
        <div class="pregame-section gt-section" v-if="hasFieldEnabled(11)">
          <div class="pregame-section__header">
            <div class="pregame-section__label">
              <i class="tim-icons icon-chart-bar-32 text-white"></i>
              Team experience
            </div>
          </div>
          <div class="experience-options">
            <div
              v-for="option in experienceOptions"
              :key="option.value"
              class="experience-option"
              :class="{ 'experience-option--active': model.team_experience === option.value }"
              @click="model.team_experience = option.value"
            >
              <span class="experience-option__label">{{ option.label }}</span>
              <span class="experience-option__desc">{{ option.desc }}</span>
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
  import { BaseRadio } from 'src/components/index';
  import {Table, TableColumn, TimeSelect, Popover, TimePicker, Switch, Dropdown, DropdownMenu, DropdownItem} from 'element-ui';
  import { API_LOCATION } from '../../../api-config'
  import { tagChipStyle } from '@/util/tagChip'
  import { Auth } from 'aws-amplify'
  import swal from "sweetalert2";
  const axios = require('axios')


  export default {
      name: "PreGameNoteStep",
      props: {
        gameInstanceId: { type: [String, Number], default: null }
      },
      components: {
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [TimeSelect.name]: TimeSelect,
        [Popover.name]: Popover,
        [TimePicker.name]: TimePicker,
        [Switch.name]: Switch,
        [Dropdown.name]: Dropdown,
        [DropdownMenu.name]: DropdownMenu,
        [DropdownItem.name]: DropdownItem,
        BaseRadio
      },
      data() {
        return {
          selectedUser: [],
          optional_fields: [],
          picker_time_format: 'hh:mm A',
          time_format: JSON.parse(localStorage.getItem('group')).time_format,
          k: 0,
          j:0,
          fields: ['name'],
          experienceOptions: [
            { value: 'New', label: 'New', desc: 'First time playing' },
            { value: 'Some have played before', label: 'Some experience', desc: 'A few have played' },
            { value: 'Experienced', label: 'Experienced', desc: 'Most have played' },
            { value: 'Expert', label: 'Expert', desc: 'Seasoned veterans' },
          ],
          draftPopulated: false,
          groupTags: [],
          bookingTags: [],
          savingTags: false,
          tagsLoading: false,
          bookingSlotIdForTags: null,
          model: {
            start_time: '',
            pre_notes: '',
            team_experience: 'New',
            team: '',
            users: []
          },
          dd: 'AM',
          hh: '',
          mm: '',
          modelValidations: {
            start_time: '',
            pre_notes: {
              required: false,
            },
            team_experience: {
              required: false,
            },
          }
        };
      },
      computed: {
        availableTags() {
          const applied = new Set(this.bookingTags.map(t => t.id))
          return this.groupTags.filter(t => !applied.has(t.id))
        }
      },
      methods: {
        tagChipStyle,
        resetState() {
          this.draftPopulated = false
          this.model.start_time = ''
          this.model.pre_notes = ''
          this.model.team_experience = 'New'
          this.model.team = ''
          this.model.users = []
          this.model.is_corporate_booking = false
          this.selectedUser = []
          this.bookingTags = []
          this.bookingSlotIdForTags = null
          this.k++
          this.j++
        },
        draftKey(key) {
          if (!this.gameInstanceId) {
            console.warn('draftKey called with no gameInstanceId for key:', key)
            return key + '_NONE'
          }
          return key + '_' + this.gameInstanceId
        },
        getInitials(first, last) {
          return ((first || '').charAt(0) + (last || '').charAt(0)).toUpperCase()
        },
        hasFieldEnabled(fieldId){
          for(let n in this.optional_fields){
            if(this.optional_fields[n].field_id === fieldId && this.optional_fields[n].is_enabled){
              return true
            }
          }
          return false
        },
        validateTime(){
          let today = new Date()
          this.model.start_time.setMonth(today.getMonth(), today.getDate())
        },
        togglePlayer(index){
          this.$set(this.selectedUser, index, !this.selectedUser[index])
          this.k++
        },
        toggleConfirmed(destination, source){
          this.model.users[0].confirmed = false
          this.k++
        },
        getError(fieldName) {
          return this.errors.first(fieldName);
        },
        setNow () {
          this.model.start_time = new Date()
        },
        populateDraft(){
          let draft = JSON.parse(localStorage.getItem(this.draftKey('draft')))
          if(!draft){
            // No saved draft data — clear model so stale data from a
            // previous draft doesn't persist (components stay mounted via v-show)
            this.resetState()
            this.loadTags()
            return
          }
          this.loadTags()
          if(draft){
            // Check both actual_start_time (from localStorage) and start_time (from backend)
            const startTimeValue = draft.actual_start_time || draft.start_time;

            if(startTimeValue){
              let time;

              // Handle both MySQL format "2025-11-21 19:16:00" and ISO format "2025-11-21T19:16:00.000Z"
              if(typeof startTimeValue === 'string') {
                // If it contains 'T', it's ISO format
                if(startTimeValue.includes('T')) {
                  time = new Date(startTimeValue);
                  time.setHours(startTimeValue.split('T')[1].split(':')[0]);
                  time.setMinutes(startTimeValue.split('T')[1].split(':')[1]);
                } else {
                  // MySQL format "2025-11-21 19:16:00" - parse manually to preserve local time
                  const parts = startTimeValue.split(' ');
                  if(parts.length >= 2) {
                    const dateParts = parts[0].split('-'); // [2025, 11, 21]
                    const timeParts = parts[1].split(':'); // [19, 16, 00]

                    // Create Date in local timezone
                    time = new Date(
                      parseInt(dateParts[0]), // year
                      parseInt(dateParts[1]) - 1, // month (0-indexed)
                      parseInt(dateParts[2]), // day
                      parseInt(timeParts[0]), // hours
                      parseInt(timeParts[1]), // minutes
                      parseInt(timeParts[2] || 0) // seconds
                    );
                  } else {
                    time = null;
                  }
                }
              } else {
                // Fallback: already a Date object or null
                time = startTimeValue;
              }

              this.model.start_time = time;
            } else {
              this.model.start_time = null
            }
            if(draft.team_experience_level){
              if(draft.team_experience_level === 1){
                this.model.team_experience = 'New'
              } else if(draft.team_experience_level === 2){
                this.model.team_experience = 'Some have played before'
              } else if(draft.team_experience_level === 3){
                this.model.team_experience = 'Experienced'
              } else if(draft.team_experience_level === 4){
                this.model.team_experience = 'Expert'
              }
            }
            if(draft.hasOwnProperty('is_corporate_booking')){
              this.model.is_corporate_booking = Boolean(draft.is_corporate_booking)
              this.j++
            }
            if(this.model.users.length > 0){
              this.model.team = this.model.users[0].team_name
            }
            this.model.pre_notes = draft.pre_game_notes

            // Restore player confirmation state from draft
            if (draft.users && draft.users.length > 0 && this.model.users.length > 0) {
              for (let n = 0; n < this.model.users.length; n++) {
                let draftUser = draft.users.find(u => u.id === this.model.users[n].id)
                if (draftUser && draftUser.confirmed !== undefined) {
                  this.model.users[n].confirmed = draftUser.confirmed
                  this.$set(this.selectedUser, n, Boolean(draftUser.confirmed))
                }
              }
              this.k++
            }
          }
          this.draftPopulated = true
        },
        async setPreNotesData () {
          localStorage.setItem(this.draftKey('preNotes'), JSON.stringify(this.model))

          //save draft
          let companyId = localStorage.getItem('companyId')
          let token = await this.getAuthToken()
          let config = {headers: {Authorization: token}}
          let url = API_LOCATION + 'companies/' + companyId
            + '/games/instances/' + this.gameInstanceId + '/drafts'

          if(this.model.team == null){
            this.model.team = ''
          }

          let data = {
            start_time: this.model.start_time.getFullYear() + '-' + (this.model.start_time.getMonth()+1) + '-' + this.model.start_time.getDate() +
              ' ' + this.model.start_time.getHours() + ':' + this.model.start_time.getMinutes(),
            team_experience_level: this.model.team_experience,
            team_name: this.model.team,
            team_id: '',
            pre_game_notes: this.model.pre_notes,
            users: this.model.users,
            is_corporate_booking: this.model.is_corporate_booking
          }
          if(this.model.users.length > 0){
            data.team_id = this.model.users[0].team_id
          }
          for(let n in data.users){
            data.users[n].confirmed = this.selectedUser[n]
          }
          try {
            await axios.put(url, data, config)
            let draft = JSON.parse(localStorage.getItem(this.draftKey('draft')))
            draft.actual_start_time = this.model.start_time.getFullYear() + '-' +
              ('0' + (this.model.start_time.getMonth()+1)).slice(-2) + '-' + ('0' + this.model.start_time.getDate()).slice(-2) +
              'T' + ('0' + (this.model.start_time.getHours())).slice(-2) + ':' + ('0' + (this.model.start_time.getMinutes())).slice(-2) + ':00.000Z'
            draft.team_experience_level = data.team_experience_level
            draft.team_name = data.team_name
            draft.team_id = data.team_id
            draft.pre_game_notes = data.pre_game_notes
            draft.users = data.users
            draft.is_corporate_booking = data.is_corporate_booking
            localStorage.setItem(this.draftKey('draft'), JSON.stringify(draft))
            return true
          } catch (err) {
            if(err.response && err.response.status == 401){
              swal({
                title: 'Error',
                html: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
                type: 'error',
              }).then((response)=> {
                this.$router.push('/')
              })
            } else if(err.response && err.response.status == 409){
              // Retrying will not help: this instance is no longer an open
              // draft, so say so instead of the generic try-again message.
              swal({
                title: 'This event form has already been submitted',
                text: "Open the event from event history to make changes, or start a fresh tracker for the event you are running.",
                type: 'error',
                confirmButtonColor: '#1d8cf8',
                confirmButtonText: 'OK'
              })
            } else {
              swal({
                title: 'Error',
                html: 'The event details could not be saved. Please try again.',
                type: 'error',
              })
            }
            return false
          }
        },
        getAuthToken() {
          return new Promise ( function(resolve) {
            Auth.currentSession()
              .then(data =>{
                resolve(data.idToken.jwtToken)
              })
              .catch(err => console.log(err))
          })
        },
        tagsBaseUrl() {
          let companyId = localStorage.getItem('companyId')
          let companyGroupId = JSON.parse(localStorage.getItem('group')).id
          return API_LOCATION + 'companies/' + companyId + '/groups/' + companyGroupId
        },
        loadTags() {
          // Booking id comes from the saved booking-info draft; unbooked
          // games have none and the section stays hidden. getUsers() also
          // sets the id directly when the step is entered.
          this.bookingSlotIdForTags = null
          this.bookingTags = []
          try {
            let info = JSON.parse(localStorage.getItem(this.draftKey('bookingInfo')))
            if (info && info.booking && info.booking.id) {
              this.bookingSlotIdForTags = info.booking.id
            }
          } catch (e) {
            // no booking info saved
          }
          if (this.bookingSlotIdForTags) {
            this.fetchTags()
          }
        },
        async fetchTags() {
          if (!this.bookingSlotIdForTags) return
          this.tagsLoading = true
          try {
            let token = await this.getAuthToken()
            let config = { headers: { Authorization: token } }
            let catalog = await axios.get(this.tagsBaseUrl() + '/tags', config)
            this.groupTags = catalog.data || []
            let applied = await axios.get(this.tagsBaseUrl() + '/bookings/' + this.bookingSlotIdForTags + '/tags', config)
            this.bookingTags = applied.data || []
          } catch (e) {
            console.log('Tags not available:', e.message)
            this.groupTags = []
            this.bookingTags = []
          } finally {
            this.tagsLoading = false
          }
        },
        async saveTagSet(ids) {
          if (this.savingTags || !this.bookingSlotIdForTags) return
          this.savingTags = true
          let previous = this.bookingTags
          try {
            let token = await this.getAuthToken()
            let config = { headers: { Authorization: token } }
            let url = this.tagsBaseUrl() + '/bookings/' + this.bookingSlotIdForTags + '/tags'
            let response = await axios.put(url, { tag_ids: ids }, config)
            this.bookingTags = response.data.tags || []
          } catch (e) {
            this.bookingTags = previous
            this.$notify({ type: 'danger', message: 'Failed to update tags', timeout: 3000 })
          } finally {
            this.savingTags = false
          }
        },
        addTag(tagId) {
          if (this.bookingTags.some(t => t.id === tagId)) return
          this.saveTagSet([...this.bookingTags.map(t => t.id), tagId])
        },
        removeTag(tag) {
          this.saveTagSet(this.bookingTags.map(t => t.id).filter(id => id !== tag.id))
        },
        async getUsers(gameId, bookingId){
          this.loading = true
          this.bookingSlotIdForTags = bookingId
          this.fetchTags()
          var token = await this.getAuthToken()
          let config = { headers: { Authorization: token } }
          let companyId = localStorage.getItem('companyId')
          let url = API_LOCATION + 'companies/' + companyId + '/games/' + gameId +
            '/bookings/' + bookingId + '/users'
          return axios.get(url, config)
            .then(response => {
              this.loading = false
              this.model.users = response.data.users
              for(let n in this.model.users){
                this.model.users[n].confirmed = true
                this.$set(this.selectedUser, parseInt(n), true)
              }
              if(this.model.users.length > 0){
                this.model.team = this.model.users[0].team_name
              }

            })
            .catch(err => {
              this.loading = false
              console.log(err)
              if(err.response && err.response.status == 401){
                swal({
                  title: 'Error',
                  html: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
                  type: 'error',
                }).then((response)=> {
                  this.$router.push('/')
                })
              }
            })
        },
        setOptionalFields(fields){
          this.optional_fields = fields
        },
        async saveDraftToBackend() {
          this.saveToLocalDraft()
          if (!this.gameInstanceId) return
          if (!this.model.start_time || !(this.model.start_time instanceof Date)) return

          let companyId = localStorage.getItem('companyId')
          let token = await this.getAuthToken()
          let config = {headers: {Authorization: token}}
          let url = API_LOCATION + 'companies/' + companyId
            + '/games/instances/' + this.gameInstanceId + '/drafts'

          let data = {
            start_time: this.model.start_time.getFullYear() + '-' + (this.model.start_time.getMonth()+1) + '-' + this.model.start_time.getDate() +
              ' ' + this.model.start_time.getHours() + ':' + this.model.start_time.getMinutes(),
            team_experience_level: this.model.team_experience,
            team_name: this.model.team || '',
            team_id: (this.model.users.length > 0) ? this.model.users[0].team_id : '',
            pre_game_notes: this.model.pre_notes,
            users: this.model.users.map((user, i) => ({
              ...user,
              confirmed: this.selectedUser[i] || false
            })),
            is_corporate_booking: this.model.is_corporate_booking
          }
          return axios.put(url, data, config).catch(err => {
            console.log('Background draft save failed:', err)
          })
        },
        saveToLocalDraft() {
          let draft = JSON.parse(localStorage.getItem(this.draftKey('draft')))
          if (!draft) return
          if (this.model.start_time && this.model.start_time instanceof Date) {
            draft.actual_start_time = this.model.start_time.getFullYear() + '-' +
              ('0' + (this.model.start_time.getMonth()+1)).slice(-2) + '-' + ('0' + this.model.start_time.getDate()).slice(-2) +
              'T' + ('0' + (this.model.start_time.getHours())).slice(-2) + ':' + ('0' + (this.model.start_time.getMinutes())).slice(-2) + ':00.000Z'
          }
          draft.pre_game_notes = this.model.pre_notes
          draft.team_experience_level = this.model.team_experience
          draft.team_name = this.model.team
          draft.team_id = (this.model.users.length > 0) ? this.model.users[0].team_id : ''
          draft.is_corporate_booking = this.model.is_corporate_booking
          if (this.model.users && this.model.users.length > 0) {
            draft.users = this.model.users.map((user, i) => ({
              ...user,
              confirmed: this.selectedUser[i] || false
            }))
          }
          localStorage.setItem(this.draftKey('draft'), JSON.stringify(draft))
        },
        async validate() {
          if(!this.model.start_time || this.model.start_time === "NaN:"){
            this.modelValidations.start_time = 'Start time cannot be empty'
            return
          } else {
            this.modelValidations.start_time = ''
          }
          const saved = await this.setPreNotesData()
          if(!saved){
            return false
          }
          return this.$validator.validateAll().then(res => {
            this.$emit('on-validated', res, this.model);
            return res;
          });
        }
      },
    mounted() {
      if(this.time_format === 'HH:mm'){
        this.picker_time_format = 'HH:mm'
      }
    }
  }
</script>

<style scoped>

/* ========================================
   Layout
   ======================================== */
.pregame {
  padding-top: 8px;
}

/* ========================================
   Section containers — visual base from gt-section
   ======================================== */
.pregame-section__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.pregame-section__label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.92);
  letter-spacing: 0.02em;
}

.pregame-section__label i {
  font-size: 14px;
}

.pregame-section__hint {
  cursor: help;
  color: rgba(255, 255, 255, 0.3);
  font-size: 14px;
  transition: color 0.15s ease;
}

.pregame-section__hint:hover {
  color: rgba(255, 255, 255, 0.6);
}

/* ========================================
   Empty state
   ======================================== */
.pregame-empty {
  text-align: center;
  padding: 20px 0 8px;
}

.pregame-empty__icon {
  font-size: 2.5em;
  opacity: 0.5;
}

.pregame-empty__text {
  margin-top: 10px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 13px;
}

/* ========================================
   Player list
   ======================================== */
.player-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

/* Player item — layout overrides (visual base from gt-list-item) */
.player-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 5px;
}

.player-item__avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.03em;
}

.player-item--confirmed .player-item__avatar {
  background: rgba(225, 78, 202, 0.12);
  color: #e14eca;
}

.player-item__content {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.player-item__name {
  font-size: 14px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.92);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.player-item--confirmed .player-item__name {
  color: #fff;
}

.player-item__badge {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #e14eca;
  background: rgba(225, 78, 202, 0.12);
  padding: 2px 6px;
  border-radius: 3px;
  flex-shrink: 0;
}

/* ========================================
   Player confirm toggle — visual base from gt-pill-btn
   ======================================== */
.player-item__toggle {
  font-size: 12px;
  font-weight: 600;
  padding: 5px 12px;
  flex-shrink: 0;
}

.player-item__toggle i {
  font-size: 11px;
}

/* ========================================
   Team field (inside group section)
   ======================================== */
.pregame-team-field {
  margin-top: 14px;
  padding-top: 14px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.pregame-team-field__label {
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.65);
  margin-bottom: 4px;
}

/* ========================================
   Start time
   ======================================== */
.pregame-time-wrapper {
  max-width: 260px;
}

.pregame-time-group {
  display: flex;
  align-items: stretch;
}

.pregame-time-group .pregame-time-picker {
  flex: 1;
}

/* Now-append — visual base from gt-now-append */

.pregame-time-error {
  color: #ec250d;
  font-size: 12px;
  margin-top: 4px;
}

/* Textarea — visual base from gt-textarea */

/* ========================================
   Switch row (corporate booking)
   ======================================== */
.pregame-switch-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

/* ========================================
   Experience options
   ======================================== */
.experience-options {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.experience-option {
  padding: 10px 14px;
  border-radius: 5px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.03);
  cursor: pointer;
  transition: all 0.15s ease;
}

.experience-option:hover {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
}

.experience-option--active {
  border-color: #e14eca;
  background: rgba(225, 78, 202, 0.06);
}

.experience-option--active:hover {
  background: rgba(225, 78, 202, 0.1);
}

.experience-option__label {
  font-size: 13px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.92);
}

.experience-option--active .experience-option__label {
  color: #e14eca;
}

.experience-option__desc {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
  margin-left: 8px;
}

/* ========================================
   Responsive
   ======================================== */
@media (max-width: 576px) {
  .pregame-section {
    padding: 14px;
  }

  .pregame-time-wrapper {
    max-width: 100%;
  }
}
</style>

<!-- Light mode overrides (unscoped) — section base handled by gt-section -->
<style>
.white-content .pregame-section__label {
  color: #344675;
}

.white-content .pregame-section__label i {
  color: #344675;
}

.white-content .pregame-team-field {
  border-top-color: rgba(0, 0, 0, 0.08);
}

.white-content .pregame-team-field__label {
  color: rgba(0, 0, 0, 0.45);
}

.white-content .pregame-section__hint {
  color: rgba(0, 0, 0, 0.3);
}

.white-content .pregame-section__hint:hover {
  color: rgba(0, 0, 0, 0.6);
}

.white-content .pregame-empty__text {
  color: rgba(0, 0, 0, 0.5);
}

/* Player item + toggle light mode handled by gt-list-item + gt-pill-btn */

.white-content .player-item__avatar {
  background: rgba(0, 0, 0, 0.05);
  color: rgba(0, 0, 0, 0.4);
}

.white-content .player-item--confirmed .player-item__avatar {
  background: rgba(225, 78, 202, 0.1);
  color: #e14eca;
}

.white-content .player-item__name {
  color: #344675;
}

.white-content .player-item--confirmed .player-item__name {
  color: #1d253b;
}

/* Textarea light mode handled by gt-textarea */

.white-content .experience-option {
  background: rgba(0, 0, 0, 0.01);
  border-color: rgba(0, 0, 0, 0.08);
}

.white-content .experience-option:hover {
  background: rgba(0, 0, 0, 0.03);
  border-color: rgba(0, 0, 0, 0.12);
}

.white-content .experience-option--active {
  border-color: #e14eca;
  background: rgba(225, 78, 202, 0.04);
}

.white-content .experience-option--active:hover {
  background: rgba(225, 78, 202, 0.08);
}

.white-content .experience-option__label {
  color: #344675;
}

.white-content .experience-option--active .experience-option__label {
  color: #e14eca;
}

.white-content .experience-option__desc {
  color: rgba(0, 0, 0, 0.4);
}

/* Time picker input group overrides (must be unscoped for Element UI internals) */
.pregame-time-group .pregame-time-picker .el-date-editor {
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
}

.pregame-time-group .pregame-time-picker .el-input__inner,
.pregame-time-group .pregame-time-picker .form-control {
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
  border-right: none !important;
}
</style>
